import UserService from './services/UserService';
import type {
  AuthAccessTokenOptions,
  AuthLogoutOptions,
  AuthNotice,
  AuthProfileUpdatePayload,
  AuthStartLoginOptions,
  AuthState,
} from './types';
import { AuthV2Adapter } from './v2/AuthV2Adapter';

const defaultState: AuthState = {
  loading: true,
  authenticated: false,
  token: null,
  user: null,
};

class AuthClient {
  private adapter: AuthV2Adapter | null = null;

  private listeners = new Set<() => void>();

  init() {
    if (this.adapter) {
      throw new Error('Auth client has already been initialized.');
    }

    const adapter = new AuthV2Adapter();
    adapter.subscribe(() => {
      this.notify();
    });
    this.adapter = adapter;
    adapter.initialize();
    this.notify();
    return this;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): AuthState {
    return this.adapter?.getState() ?? defaultState;
  }

  async startLogin(options?: AuthStartLoginOptions) {
    return this.getAdapter().startLogin(options);
  }

  async completeLoginIfNeeded() {
    return this.getAdapter().completeLoginIfNeeded();
  }

  async getAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getAccessToken(options);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getFreshAccessToken(options);
  }

  async logout(options?: AuthLogoutOptions) {
    return this.getAdapter().logout(options);
  }

  isAuthenticated() {
    return this.getAdapter().isAuthenticated();
  }

  async updateProfile(data: AuthProfileUpdatePayload) {
    await this.getFreshAccessToken();
    await this.updateCurrentProfile(data);
    await this.getAdapter().refreshSession();
  }

  consumeNotice(): AuthNotice | null {
    return this.getAdapter().consumeNotice();
  }

  private getAdapter() {
    if (!this.adapter) {
      throw new Error('Auth client has not been initialized.');
    }

    return this.adapter;
  }

  private notify() {
    this.listeners.forEach((listener) => listener());
  }

  private createProfileService() {
    return new UserService({ baseUri: window.configs.AUTH_HOST }, false);
  }

  private async updateCurrentProfile(data: AuthProfileUpdatePayload): Promise<void> {
    const userService = this.createProfileService();

    await new Promise<void>((resolve, reject) => {
      userService.updateProfile(data, (error) => {
        if (error) {
          reject(error);
          return;
        }

        resolve();
      });
    });
  }
}

export default new AuthClient();
